import React, { useEffect, useMemo, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Alert, message, Spin, Switch } from "antd";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { rolesApi, usersApi } from "../../apis";

const adminEditSchema = yup.object({
  firstName: yup
    .string()
    .required("First name is required")
    .trim(),
  lastName: yup
    .string()
    .required("Last name is required")
    .trim(),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is required")
    .trim(),
  phone: yup
    .string()
    .nullable()
    .optional()
    .test(
      "phone-format",
      "Invalid phone number. Enter 9 digits after +20 (e.g., 1001234567)",
      (value) => {
        if (!value) return true;
        return /^\d{9}$/.test(value);
      },
    ),
  role: yup.string().required("Role is required"),
  isActive: yup.boolean().optional(),
  password: yup
    .string()
    .optional()
    .min(6, "Minimum 6 characters")
    .transform((value) => (value === "" ? undefined : value)),
});

const titleCase = (value = "") =>
  value
    .split(/[_\-.]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const extractRoleName = (role) =>
  ((typeof role === "string" ? role : role?.name || "") || "").toLowerCase();

// Check if a role has any dashboard permissions
const isDashboardRole = (role) => {
  if (!role || !role.permissions) return false;
  // Check if role has any view, create, edit, or delete permissions
  const dashboardPermissions = [
    "users.view",
    "users.create",
    "users.edit",
    "users.delete",
    "roles.view",
    "roles.create",
    "roles.edit",
    "roles.delete",
    "permissions.view",
    "permissions.manage",
    "boat.view",
    "boat.create",
    "boat.edit",
    "boat.delete",
    "trips.view",
    "trips.create",
    "trips.edit",
    "trips.delete",
    "schedules.view",
    "schedules.create",
    "schedules.edit",
    "schedules.delete",
    "pricing.view",
    "pricing.create",
    "pricing.edit",
    "pricing.delete",
    "reservations.view",
    "reservations.create",
    "reservations.edit",
    "reservations.refund",
    "reservations.delete",
    "payments.view",
    "payments.manage",
    "tickets.view",
    "tickets.check_in",
    "reports.view",
  ];

  return role.permissions.some((permission) =>
    dashboardPermissions.includes(permission.name),
  );
};

const extractLocalNumber = (fullPhoneNumber) => {
  if (!fullPhoneNumber) return "";
  if (fullPhoneNumber.startsWith("+20")) {
    return fullPhoneNumber.substring(3);
  }
  return fullPhoneNumber;
};

const EditAdmin = () => {
  const { id } = useParams();
  const history = useHistory();

  const [menu, setMenu] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [localPhoneNumber, setLocalPhoneNumber] = useState("");
  const [roles, setRoles] = useState([]);
  const [originalRole, setOriginalRole] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(adminEditSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "",
      isActive: true,
      password: "",
    },
    mode: "onChange",
  });

  const isActive = watch("isActive");
  const selectedRole = watch("role");
  const selectedRoleDetails = useMemo(
    () => roles.find((role) => role.name === selectedRole) || null,
    [roles, selectedRole],
  );

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length > 9) {
      value = value.slice(0, 9);
    }
    setLocalPhoneNumber(value);
    setValue("phone", value, { shouldValidate: true });

    if (errors.phone) {
      clearErrors("phone");
    }
  };

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const [allRoles, user] = await Promise.all([
          rolesApi.getAll(),
          usersApi.getById(id),
        ]);
        if (!active) return;

        const userRoleName = extractRoleName(user.role);
        if (userRoleName === "customer") {
          setNotFound(true);
          return;
        }

        // Store original role
        setOriginalRole(userRoleName);

        // Filter assignable roles - only dashboard roles (not customer)
        const assignableRoles = (allRoles || [])
          .filter((role) => {
            // Don't allow changing to customer role
            if (role.name === "customer") return false;
            // Only allow roles that have dashboard permissions
            return isDashboardRole(role);
          })
          .sort((a, b) => a.name.localeCompare(b.name));

        setRoles(assignableRoles);
        setValue("firstName", user.firstName || "");
        setValue("lastName", user.lastName || "");
        setValue("email", user.email || "");
        setValue("role", userRoleName || "", { shouldValidate: true });

        const localNumber = extractLocalNumber(user.phone || "");
        setValue("phone", localNumber);
        setLocalPhoneNumber(localNumber);

        setValue("isActive", user.isActive !== false);
        setValue("password", "");
      } catch (error) {
        if (!active) return;
        console.error("Error loading user data:", error);
        setNotFound(true);
      } finally {
        if (active) setFetching(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [id, setValue]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      // Validate phone number format
      if (data.phone && !/^\d{9}$/.test(data.phone)) {
        setError("phone", {
          type: "manual",
          message:
            "Invalid phone number. Enter 9 digits after +20 (e.g., 1001234567)",
        });
        setLoading(false);
        return;
      }

      const payload = {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        phone: data.phone ? `+20${data.phone}` : undefined,
        isActive: data.isActive,
      };

      // Only include role if it has changed
      if (data.role !== originalRole) {
        payload.role = data.role;
      }

      // Only include password if it was entered
      if (data.password?.trim()) {
        payload.password = data.password.trim();
      }

      await usersApi.update(id, payload);
      message.success("User updated successfully");
      history.push("/users/admins");
    } catch (err) {
      console.error("Error updating user:", err);
      const msg = err.response?.data?.message;
      if (Array.isArray(msg)) {
        msg.forEach((m) => message.error(m));
      } else {
        message.error(msg || "Failed to update user");
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="main-wrapper">
        <Header onMenuClick={() => {}} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <Spin size="large" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="main-wrapper">
        <Header onMenuClick={() => {}} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert message="Dashboard user not found." type="error" showIcon />
          </div>
        </div>
      </div>
    );
  }

  const fullName = `${watch("firstName")} ${watch("lastName")}`;

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>
                    Edit Dashboard User — {fullName.trim() || "Loading..."}
                  </h5>
                  <button
                    type="button"
                    className="btn btn-primary cancel"
                    onClick={() => history.push("/users/admins")}
                    disabled={loading}
                  >
                    Back
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="form-group-item">
                  <h5 className="form-title">Personal Information</h5>
                  <div className="row">
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          First Name <span className="text-danger">*</span>
                        </label>
                        <input
                          className={`form-control ${
                            errors.firstName ? "is-invalid" : ""
                          }`}
                          {...register("firstName")}
                          disabled={loading}
                        />
                        {errors.firstName && (
                          <small className="text-danger d-block mt-1">
                            {errors.firstName.message}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Last Name <span className="text-danger">*</span>
                        </label>
                        <input
                          className={`form-control ${
                            errors.lastName ? "is-invalid" : ""
                          }`}
                          {...register("lastName")}
                          disabled={loading}
                        />
                        {errors.lastName && (
                          <small className="text-danger d-block mt-1">
                            {errors.lastName.message}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>Phone</label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <span className="me-1">🇪🇬</span> +20
                          </span>
                          <input
                            type="tel"
                            className={`form-control ${
                              errors.phone ? "is-invalid" : ""
                            }`}
                            placeholder="1001234567"
                            value={localPhoneNumber}
                            onChange={handlePhoneChange}
                            disabled={loading}
                          />
                        </div>
                        {errors.phone && (
                          <small className="text-danger d-block mt-1">
                            {errors.phone.message}
                          </small>
                        )}
                        <small className="text-muted d-block mt-1">
                          Enter 9 digits after +20 (e.g., 1001234567)
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-group-item mt-2">
                  <h5 className="form-title">Account Details</h5>
                  <div className="row">
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Email <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className={`form-control ${
                            errors.email ? "is-invalid" : ""
                          }`}
                          {...register("email")}
                          disabled={loading}
                        />
                        {errors.email && (
                          <small className="text-danger d-block mt-1">
                            {errors.email.message}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>New Password</label>
                        <input
                          type="password"
                          className={`form-control ${
                            errors.password ? "is-invalid" : ""
                          }`}
                          placeholder="Leave blank to keep current"
                          {...register("password")}
                          disabled={loading}
                          autoComplete="new-password"
                        />
                        {errors.password && (
                          <small className="text-danger d-block mt-1">
                            {errors.password.message}
                          </small>
                        )}
                        <small className="text-muted d-block mt-1">
                          Leave blank to keep the current password
                        </small>
                      </div>
                    </div>

                    <div className="col-lg-2 col-md-4">
                      <div className="input-block mb-3">
                        <label>
                          Role <span className="text-danger">*</span>
                        </label>
                        <select
                          className={`form-control ${
                            errors.role ? "is-invalid" : ""
                          }`}
                          {...register("role")}
                          disabled={loading}
                        >
                          {roles.map((role) => (
                            <option key={role.id} value={role.name}>
                              {titleCase(role.name)}
                            </option>
                          ))}
                        </select>
                        {errors.role && (
                          <small className="text-danger d-block mt-1">
                            {errors.role.message}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="col-lg-2 col-md-4">
                      <div className="input-block mb-3">
                        <label>Account Status</label>
                        <div style={{ paddingTop: 8 }}>
                          <Switch
                            checked={isActive}
                            onChange={(v) => setValue("isActive", v)}
                            checkedChildren="Active"
                            unCheckedChildren="Inactive"
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedRoleDetails && (
                  <Alert
                    className="mb-4"
                    type="info"
                    showIcon
                    message={`Selected role: ${titleCase(
                      selectedRoleDetails.name,
                    )}`}
                    description={
                      selectedRoleDetails.description ||
                      `${titleCase(selectedRoleDetails.name)} has ${
                        (selectedRoleDetails.permissions || []).length
                      } dashboard permissions.`
                    }
                  />
                )}

                <div className="form-group-item border-0">
                  <div className="text-end">
                    <button
                      type="button"
                      className="btn btn-primary cancel me-2"
                      onClick={() => history.push("/users/admins")}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Saving…
                        </>
                      ) : (
                        "Save Changes"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditAdmin;